import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiShield, FiSearch, FiCamera, FiX, FiAlertTriangle, FiInfo } from 'react-icons/fi';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';

// BarcodeDetector é nativo do navegador (Chrome/Edge/Android). Não há tipos no DOM lib.
// deno-lint-ignore no-explicit-any
type BarcodeDetectorCtor = new (opts?: { formats?: string[] }) => {
  detect: (src: CanvasImageSource) => Promise<Array<{ rawValue: string }>>;
};

/** Extrai o código (e hash opcional) de um texto que pode ser a URL completa do QR
 *  (…/verificar/CODIGO?h=HASH) ou apenas o código digitado. */
function parseEntrada(texto: string): { codigo: string; hash?: string } | null {
  const t = texto.trim();
  if (!t) return null;
  // É uma URL de verificação?
  const m = t.match(/\/verificar\/([^/?#]+)(?:\?h=([^&#]+))?/i);
  if (m) return { codigo: decodeURIComponent(m[1]), hash: m[2] ? decodeURIComponent(m[2]) : undefined };
  // Senão, trata como código digitado (remove espaços/hífens de digitação).
  const cod = t.replace(/[\s-]/g, '').toUpperCase();
  return cod ? { codigo: cod } : null;
}

export function VerificarEntradaPage() {
  const navigate = useNavigate();
  const [codigo, setCodigo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [lendo, setLendo] = useState(false);
  const [suportaCamera, setSuportaCamera] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    setSuportaCamera('BarcodeDetector' in window && !!navigator.mediaDevices?.getUserMedia);
    return () => pararCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function irPara(entrada: { codigo: string; hash?: string }) {
    const qs = entrada.hash ? `?h=${encodeURIComponent(entrada.hash)}` : '';
    navigate(`/verificar/${encodeURIComponent(entrada.codigo)}${qs}`);
  }

  function enviar(e: React.FormEvent) {
    e.preventDefault();
    setErro(null);
    const parsed = parseEntrada(codigo);
    if (!parsed) { setErro('Informe o código de verificação ou cole o link do QR Code.'); return; }
    irPara(parsed);
  }

  function pararCamera() {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setLendo(false);
  }

  async function iniciarCamera() {
    setErro(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      setLendo(true);
      const video = videoRef.current!;
      video.srcObject = stream;
      await video.play();

      const Detector = (window as unknown as { BarcodeDetector: BarcodeDetectorCtor }).BarcodeDetector;
      const detector = new Detector({ formats: ['qr_code'] });

      const tick = async () => {
        if (!streamRef.current) return;
        try {
          const codes = await detector.detect(video);
          if (codes.length > 0) {
            const parsed = parseEntrada(codes[0].rawValue);
            pararCamera();
            if (parsed) { irPara(parsed); return; }
          }
        } catch { /* frame sem leitura */ }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch {
      setErro('Não foi possível acessar a câmera. Verifique a permissão ou digite o código manualmente.');
      pararCamera();
    }
  }

  return (
    <PortalPage
      titulo="Verificar autenticidade"
      subtitulo="Confirme se um documento emitido pela Prefeitura é autêntico e não foi alterado."
      icone={<FiShield size={26} />}
      largura="estreita"
      breadcrumbs={[{ label: 'Início', href: '/' }, { label: 'Verificar autenticidade' }]}
    >
      <div className="space-y-5">
        <SectionCard titulo="Digite o código" icone={<FiSearch size={18} />}>
          <form onSubmit={enviar} className="space-y-3">
            <p className="text-sm text-slate-600">
              O código de verificação está no rodapé do documento, ao lado do QR Code.
            </p>
            <div className="flex gap-2">
              <input
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                placeholder="Ex.: ABCD2K7M9P"
                className="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-mono uppercase tracking-wider outline-none focus:border-institutional"
                autoComplete="off"
                spellCheck={false}
              />
              <Button type="submit"><FiSearch size={15} /> Verificar</Button>
            </div>
            {erro && (
              <Alert variante="erro" className="flex items-start gap-2">
                <FiAlertTriangle className="flex-shrink-0 mt-0.5" size={14} /> <span>{erro}</span>
              </Alert>
            )}
          </form>
        </SectionCard>

        <SectionCard titulo="Ler o QR Code" icone={<FiCamera size={18} />}>
          {lendo ? (
            <div className="space-y-3">
              <div className="relative overflow-hidden rounded-xl bg-slate-900 aspect-video">
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video ref={videoRef} className="h-full w-full object-cover" playsInline muted />
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="h-44 w-44 rounded-2xl border-4 border-white/70" />
                </div>
              </div>
              <p className="text-center text-xs text-slate-500">Aponte a câmera para o QR Code do documento.</p>
              <Button variant="outline" onClick={pararCamera} className="w-full"><FiX size={15} /> Parar câmera</Button>
            </div>
          ) : suportaCamera ? (
            <div className="space-y-2">
              <p className="text-sm text-slate-600">Use a câmera do dispositivo para ler o QR Code do rodapé.</p>
              <Button variant="outline" onClick={iniciarCamera} className="w-full"><FiCamera size={15} /> Abrir câmera</Button>
            </div>
          ) : (
            <Alert variante="info" className="flex items-start gap-2">
              <FiInfo className="flex-shrink-0 mt-0.5" size={14} />
              <span>
                A leitura pela câmera não é suportada neste navegador. Use o aplicativo de câmera do seu celular
                para escanear o QR Code (ele abre esta página automaticamente) ou digite o código acima.
              </span>
            </Alert>
          )}
        </SectionCard>
      </div>
    </PortalPage>
  );
}
